import React, {Fragment, useCallback, useState} from 'react';
import createForm from "./Form";
import TextInput from "../../form/TextInput";
import Select from "../../form/Select";
import { ifCompletedInput } from "../../../utils/other";
import classNames from 'classnames';
import Modal from "../../layout/Modal";
import Portal from "../../layout/Portal";
import { UnitFormatter, remoteLanguageFormatter, CurrencyFormatter, PartnerSelect} from "../../../utils/formatters";
import Invoice from "../OperationList/Elements/Invoice";
import get from "../../../utils/get";
import {loadComponentById} from "../../../utils/components";
import { 
    PAYMENT_PRICE_TYPE_UNIT_NET_PRICE,
    calculateItemPrice
} from "../../classes/Price";
import DateTimeInput from "../../form/DateTimeInput";
import isEqual from "lodash-es/isEqual";
import {sendRequest} from "../../../utils/request";
import moment from "moment";
import PrintButton from "../../form/PrintButton";
import {componentFilter} from "../../../utils/filters";
import Confirm, {smallDeleteProps} from "../../form/Confirm";
import DataGrid from "../../form/SelectorGrid/DataGrid";
import set from "../../../utils/set";

const sortable = true;
const filterable = true;

const setPrice = ({editItems, value, field, row, rowKey}) => {
    let oldItem = {...row};
    let newItem = {...row};
    set(newItem, field, value);
    if (!newItem.calculator && !oldItem.vat) {
        oldItem = {...newItem};
    }
    if (newItem.vat) {
        const saleprice = calculateItemPrice("saleprice", field, newItem, oldItem, PAYMENT_PRICE_TYPE_UNIT_NET_PRICE);
        saleprice.setPrice(newItem.saleprice);
        const listprice = calculateItemPrice("listprice", field, newItem, oldItem, PAYMENT_PRICE_TYPE_UNIT_NET_PRICE);
        listprice.setPrice(newItem.listprice);
        saleprice.boundTo(newItem, "saleprice");
        listprice.boundTo(newItem, "listprice");
        newItem.saleprice = saleprice.getPrice();
        newItem.listprice = listprice.getPrice();
    }
    if (editItems) {
        editItems({[rowKey]: newItem});
    }
    return newItem;
}

const PriceField = (props) => {
    return !props.row.calculator ?
        <TextInput
            type="decimal"
            name={props.field}
            label={false}
            focus
            value={props.value}
            onKeyDown={(e, field, value) => {
                if (ifCompletedInput(e)) {
                    setPrice({...props, value});
                }
            }}
            onBlur={(e, field, value) => {
                setPrice({...props, value});
            }}
        /> : CurrencyFormatter(props.value);
}

const QtyField = (props) => {
    return !props.row.calculator ?
        <TextInput
            type="decimal"
            name={props.field}
            label={false}
            focus
            value={props.value}
            onKeyDown={(e, field, value) => {
                if (ifCompletedInput(e)) {
                    setPrice({...props, value});
                }
            }}
            onBlur={(e, field, value) => {
                setPrice({...props, value});
            }}
        /> : UnitFormatter(props.value, ' ');
}

const PriceVatRateField = (props) => {
    return !props.row.calculator ?
        <Select
            name={props.field}
            component="vatrate"
            filter={{field: "active", value: 1}}
            objectValue={true}
            label={false}
            storeType="local"
            value={props.value}
            view={["rate"]}
            saveChanges={(field, value) => {
                setPrice({...props, value});
            }}
        /> : <span
            className={classNames("vat", {"text-center": !props.value})}>{props.value?.name || "!"}</span>
}

const TextInputField = ({editItems, value, field, row, rowKey}) =>
    <TextInput
        name={field}
        label={false}
        focus
        value={value}
        onKeyDown={(e, field, value) => {
            if (ifCompletedInput(e)) {
                row = {...row};
                row[field] = value;
                editItems({[rowKey]: row});
            }
        }}
        onBlur={(e, field, value) => {
            row = {...row};
            row[field] = value;
            editItems({[rowKey]: row});
        }}
    />

const AddItemHeader = (props) => {
    const startdate = props.values.startdate || moment().subtract(1, "months").startOf('month').format("YYYY-MM-DD") + " 00:00:00";
    const enddate = props.values.enddate || moment().subtract(1, "months").endOf('month').format("YYYY-MM-DD") + " 23:59:59";
    const state = parseInt(props.values.state || "0");
    const [invoicingItemType, setInvoiceItemType] = useState(null);
    const [addItem, setAddItem] = useState(false);
    const [item, setItem] = useState(null);
    const addNewItem = useCallback((item) => {
        if (item.vat) {
            item = setPrice({field: 'qty', value: item.qty, row: item});
        }
        props.addItems([item]);
    }, []);

    const loadItem = useCallback(async (value, values) => {
        let item = await loadComponentById(value.component_type, value.component_id, ['id', 'name', 'calculator', 'unit', 'listprice', 'vat', 'currency', 'comment', "qty", "saleprice", "calculator"], {
            invoicing: {...props.values, ...values, customer: props.values.customer.partner_id, seller: props.context.partner.partner_id}
        });
        item = setPrice({field: "saleprice", value: item.saleprice, row: item});
        item = setPrice({field: "listprice", value: item.saleprice, row: item});
        value = { ...value, ...item, component_type: value.component_type, component_id: value.component_id };
        addNewItem(value);
    }, []);
    return <Portal portalId={props.portalId}>
        {state === 0 && <div className="row form-row">
            <div className="col-md-6">
                <Select
                    name="invoicingitem_type"
                    objectValue={false}
                    labelKey="component"
                    valueKey="component"
                    saveChanges={async (field, value) => {
                        if (value === 'DummyAdapterItem') {
                            let item = {component_type: value};
                            props.addItems([item]);
                            setInvoiceItemType(null);
                        } else {
                            setInvoiceItemType(value);
                        }
                    }}
                    storeType="local"
                    optionsTemplate={(props) => {
                        return remoteLanguageFormatter("invoicingitem_type")(props[props.valueKey])
                    }}
                    selectedOptionTemplate={(props) => {
                        return remoteLanguageFormatter("invoicingitem_type")(props[props.valueKey])
                    }}
                    value={invoicingItemType}
                    options={props.invoicing_types}
                    label="Számla elem hozzáadása"
                />
            </div>
            <Portal>
                <Modal
                    theme="default"
                    state={!!addItem}
                    header="Tétel hozzáadás"
                    buttons={[
                        {
                            text: "Mégse",
                            props: {color: "default", disabled: props.isLoading},
                            onClose: true,
                            onClick: async () => {
                                setAddItem(false);
                                setItem(null);
                            }
                        },
                        {
                            text: "Mehet",
                            props: {color: "primary", disabled: props.isLoading},
                            onClick: async () => {
                                await props.setFieldValue("startdate", startdate);
                                await props.setFieldValue("enddate", enddate);
                                await loadItem(item, {startdate, enddate});
                                setAddItem(false);
                                setItem(null);
                            }
                        }
                    ]}
                >
                    <div className="row">
                        <div className="col-md-6">
                            <DateTimeInput
                                name="startdate"
                                value={startdate}
                                initialValue={moment().subtract(1, "months").startOf('month').format("YYYY-MM-DD") + " 00:00:00"}
                                label="Időszak"
                                showTime={false}
                                saveChanges={(field, value) => props.setFieldValue(field, value + " 00:00:00")}
                            />
                        </div>
                        <div className="col-md-6">
                            <DateTimeInput
                                name="enddate"
                                value={enddate}
                                initialValue={moment().subtract(1, "months").endOf('month').format("YYYY-MM-DD") + " 23:59:59"}
                                label={<Fragment>&nbsp;</Fragment>}
                                showTime={false}
                                saveChanges={(field, value) => props.setFieldValue(field, value + " 23:59:59")}
                            />
                        </div>
                    </div>
                </Modal>
            </Portal>
            {invoicingItemType &&
            <div className="col-md-6">
                <Select
                    name="invoicingitem_template"
                    objectValue={true}
                    component={invoicingItemType}
                    filter={{
                        field: "customer", value: props.values.customer.partner_id
                    }}
                    labelKey="name"
                    valueKey="id"
                    saveChanges={async (field, value) => {
                        if (!!value.calculator) {
                            if (!props.calculator) {
                                setInvoiceItemType(null);
                                setAddItem(true);
                                setItem({component_type: invoicingItemType, component_id: value.id});
                                return;
                            } else {
                                const component_type = invoicingItemType;
                                setInvoiceItemType(null);
                                await loadItem({component_type, component_id: value.id});
                                return;
                            }
                        } else {
                            value = { ...value, qty: 1, saleprice: value.listprice, component_type: invoicingItemType, component_id: value.id };
                        }
                        delete value.id;
                        if (value) {
                            addNewItem(value);
                        }
                        setInvoiceItemType(null);
                    }}
                    view={['id', 'name', 'calculator', 'unit', 'listprice', 'vat', 'currency', 'comment']}
                    storeType="local"
                    label="Számla elem típusa"
                />
            </div>
            }
        </div>}
    </Portal>
}

class InvoicingForm extends React.Component {

    state = {
        calculator: null,
    }

    portalId = "invoice-item" + (new Date()).getTime();

    getValue = (name) => {
        if (name === "invoice.comment") {
            name = "comment";
        }
        return get(this.props.values, name, undefined);
    }

    getProps = (name) => {
        let props = {...this.props.getFieldProps(name)};
        props = {...props, ...this.props[name]};
        if (!props.disabled) {
            const disabled = this.props.disabled;//Array.isArray(this.props.disabled) ? this.props.disabled : [this.props.disabled];
            props.disabled = (!Array.isArray(disabled) && disabled) || (Array.isArray(disabled) && disabled.rowKeyOf(name) !== -1);
            if (this.props[name]) {
                props = {
                    ...this.props[name],
                    ...props,
                    disabled: this.props[name].disabled || props.disabled
                };
            }
        }
        return props;
    }

    setPrice = (field, newItem, oldItem) => {
        const saleprice = calculateItemPrice("saleprice", field, newItem, oldItem, PAYMENT_PRICE_TYPE_UNIT_NET_PRICE);
        saleprice.setPrice(newItem.saleprice);
        const listprice = calculateItemPrice("listprice", field, newItem, oldItem, PAYMENT_PRICE_TYPE_UNIT_NET_PRICE);
        listprice.setPrice(newItem.listprice);
        saleprice.boundTo(newItem, "saleprice");
        listprice.boundTo(newItem, "listprice");
        newItem.saleprice = saleprice.getPrice();
        newItem.listprice = listprice.getPrice();
        return newItem;
    }

    componentDidMount() {
        this.loadIsCalculator();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            !isEqual(this.props.values[this.props.componentConfig.idfield], prevProps.values[prevProps.componentConfig.idfield])
            ||
            !isEqual(this.props.values.items, prevProps.values.items)
        ) {
            this.loadIsCalculator();
        }
    }

    loadIsCalculator = () => {
        this.itemsRequest && this.itemsRequest.abort();
        this.setState({
            calculator: false
        }, () => {
            if (this.props.values[this.props.componentConfig.idfield]) {
                this.itemsRequest = sendRequest({
                    option: "componentlist",
                    parentcomponent: this.props.componentConfig.componentname,
                    parentid: this.props.values[this.props.componentConfig.idfield],
                    listfield: "items",
                    filter: [{field: "calculator", operator: "not isnull"}],
                    withheader: 1,
                    itemsperpage: 1,
                }, {showError: false});
                this.itemsRequest.then((data) => {
                    this.setState({calculator: !!data.items.length});
                });
            } else {
                this.setState({calculator: !!(this.props.values.items || []).find((item) => !!item.calculator)});
            }
        });
    }

    render() {
        return (
            <form onSubmit={this.props.handleSubmit}>
                <div className="row form-row">
                    <div className="col-md-12">
                        <Select
                            label="Vevő"
                            name="customer"
                            component="partner"
                            optionsTemplate={PartnerSelect}
                            objectValue={true}
                            labelKey="name"
                            valueKey="partner_id"
                            storeType="remote"
                            value={this.props.values.customer}
                            saveChanges={this.props.setFieldValue}
                            { ...this.props.getFieldProps('customer') }
                        />
                    </div>
                </div>
                <div className="row form-row">
                    <div className="col-md-12">
                        <Invoice props={{
                            values: {...this.props.values, seller: this.props.context.partner},
                            getProps: this.getProps, 
                            getValue: this.getValue, 
                            setFieldValue: (field, value) => {if(field === "invoice.comment") field = "comment"; this.props.setFieldValue(field, value);}
                        }} default="seller" { ...this.props.getFieldProps('seller') } />
                    </div>
                </div>
                {this.props.values.customer &&
                <Fragment>
                    {
                        this.state.calculator ? <div className="row form-row">
                            <div className="col-md-6">
                                <DateTimeInput
                                    {...this.props.getFieldProps("startdate")}
                                    name="startdate"
                                    value={this.props.values.startdate}
                                    label="Időszak"
                                    showTime={false}
                                    disabled={true}
                                    saveChanges={this.props.setFieldValue}
                                />
                            </div>
                            <div className="col-md-6">
                                <DateTimeInput
                                    {...this.props.getFieldProps("enddate")}
                                    name="enddate"
                                    value={this.props.values.enddate}
                                    label={<Fragment>&nbsp;</Fragment>}
                                    showTime={false}
                                    disabled={true}
                                    saveChanges={this.props.setFieldValue}
                                />
                            </div>
                        </div> : null
                    }
                    <div id={this.portalId} />
                    <div className="row form-row">
                        <div className="col-sm-12">
                            <label className="control-label">Tételek</label>
                            <DataGrid
                                ref={(e) => this.items = e}
                                className="p-small-table"
                                componentConfig={this.props.componentConfig}
                                name="items"
                                disabled={this.props.getFieldProps("items").disabled}
                                data={this.props.values}
                                value={this.props.values.items}
                                saveChanges={this.props.setFieldValue}
                                addItems={(addItems) => {
                                    if (!this.props.values[this.props.componentConfig.idfield]) {
                                        let items = [...(this.props.values.items || [])];
                                        addItems.forEach((item) => {
                                            items.push(item);
                                        });
                                        return items;
                                    } else {
                                        return addItems;
                                    }
                                }}
                                editItems={(editedItems) => {
                                    if (!this.props.values[this.props.componentConfig.idfield]) {
                                        let items = [...(this.props.values.items || [])];
                                        editedItems.forEach((item, idx) => {
                                            items[idx] = item;
                                        });
                                        return items;
                                    } else {
                                        return editedItems;
                                    }
                                }}
                                removeItems={(removedItems) => {
                                    if (!this.props.values[this.props.componentConfig.idfield]) {
                                        let items = [...(this.props.values.items || [])];
                                        removedItems.forEach((item, idx) => {
                                            items.splice(idx, 1);
                                        });
                                        return items;
                                    } else {
                                        return removedItems;
                                    }
                                }}
                                view={["report_component_id","saleprice"]}
                                fields={{
                                    "action": {
                                        props: {
                                            header: AddItemHeader,
                                            width: "auto",
                                            calculator: this.state.calculator,
                                            portalId: this.portalId,
                                            values: this.props.values,
                                            context: this.props.context,
                                            invoicing_types: this.props.invoicing_types,
                                            setFieldValue: this.props.setFieldValue,
                                            formatter: ({row, rowKey, removeItems}) => {
                                                if (parseInt(this.props.values.state || "0") !== 0) return null;
                                                return <Confirm
                                                    {...smallDeleteProps}
                                                    onAccept={() => {
                                                        removeItems({[rowKey]: row});
                                                    }}
                                                />
                                            }
                                        }
                                    },
                                    "name": {
                                        props: {
                                            header: 'Megnevezés',
                                            width: 5,
                                            order: "ASC",
                                            sortable,
                                            filterable,
                                            className: ({value}) => {
                                                return {"danger color bold": !value};
                                            },
                                            editable: TextInputField
                                        }
                                    },
                                    "vat": {
                                        props: {
                                            header: "Áfa",
                                            width: "auto",
                                            sortable,
                                            filterable: componentFilter({component: "vatrate"}),
                                            className: ({value}) => {
                                                return {"danger color bold": !value};
                                            },
                                            formatter: ({value}) => <span
                                                className={classNames("vat", {"text-center": !value})}>{value?.name || "!"}</span>,
                                            editable: PriceVatRateField
                                        }
                                    },
                                    "listprice": {
                                        props: {
                                            header: 'Lista ár',
                                            width: 1,
                                            sortable,
                                            filterable,
                                            formatter: ({value}) => CurrencyFormatter(value),
                                            editable: PriceField
                                        }
                                    },
                                    "saleprice_net": {
                                        props: {
                                            header: 'Nettó egységár',
                                            width: 1,
                                            sortable,
                                            filterable,
                                            formatter: ({value}) => CurrencyFormatter(value),
                                            className: ({value}) => {
                                                return {"currency": true, "danger color bold": !value};
                                            },
                                            editable: PriceField
                                        }
                                    },
                                    "saleprice_gross": {
                                        props: {
                                            header: 'Bruttó egységár',
                                            width: 1,
                                            sortable,
                                            filterable,
                                            formatter: ({value}) => CurrencyFormatter(value),
                                            className: ({value}) => {
                                                return {"currency": true, "danger color bold": !value};
                                            },
                                            editable: PriceField
                                        }
                                    },
                                    "qty": {
                                        props: {
                                            header: 'Összesen',
                                            width: 1,
                                            sortable,
                                            filterable,
                                            className: ({value}) => {
                                                return {"unit": true, "danger color bold": !value};
                                            },
                                            formatter: ({value}) => UnitFormatter(value, ' '),
                                            editable: QtyField
                                        }
                                    },
                                    "unit": {
                                        props: {
                                            header: 'Egység',
                                            width: "auto",
                                            sortable,
                                            filterable,
                                            className: ({value}) => {
                                                return {"danger color bold": !value}
                                            },
                                            editable: TextInputField
                                        }
                                    },
                                    "saleprice_net_total": {
                                        props: {
                                            header: 'Nettó összeg',
                                            width: 1,
                                            sortable,
                                            filterable,
                                            formatter: ({value}) => CurrencyFormatter(value),
                                            className: ({value}) => {
                                                return {"currency": true, "danger color bold": !value}
                                            },
                                            editable: PriceField
                                        }
                                    },
                                    "saleprice_gross_total": {
                                        props: {
                                            header: 'Bruttó összeg',
                                            width: 1,
                                            sortable,
                                            filterable,
                                            formatter: ({value}) => CurrencyFormatter(value),
                                            className: ({value}) => {
                                                return {"currency": true, "danger color bold": !value}
                                            },
                                            editable: PriceField
                                        }
                                    },
                                    "comment": {
                                        props: {
                                            header: 'Megjegyzés',
                                            width: 2,
                                            sortable,
                                            filterable,
                                            editable: TextInputField
                                        }
                                    },
                                    "report_component_type": {
                                        props: {
                                            header: 'Riport',
                                            width: "auto",
                                            formatter: ({row}) => !!row.report_component_type && !!row.report_component_id &&
                                                <PrintButton
                                                    component_type={row.report_component_type}
                                                    component_id={row.report_component_id}
                                                    type="excel"
                                                    icon={{lib: "fas", name: "download"}}
                                                >
                                                    Letöltés
                                                </PrintButton>
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </Fragment>
                }
            </form>
        );
    }
}

export default createForm(InvoicingForm);
